import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaCode,
  FaEye,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const AdminDomains = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#ff6a00",
    tags: "",
    order: 0,
  });

  useEffect(() => {
    setShowFooter(false);
    fetchDomains();
    checkDarkMode();
    return () => setShowFooter(true);
  }, []);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roadmap/admin/domains", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains(response.data.domains);
    } catch (error) {
      console.error("Fetch domains error:", error);
      toast.error("Failed to fetch domains");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      toast.error("Name and description are required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const data = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        tags: formData.tags
          ? formData.tags.split(",").map((t) => t.trim())
          : [],
        order: formData.order || 0,
      };

      if (editingDomain) {
        await axios.put(
          `/api/roadmap/admin/domains/${editingDomain._id}`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Domain updated");
      } else {
        await axios.post("/api/roadmap/admin/domains", data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Domain created");
      }
      resetForm();
      fetchDomains();
    } catch (error) {
      console.error("Save domain error:", error);
      toast.error(error.response?.data?.message || "Failed to save domain");
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Delete "${domain.name}" and all its topics?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/roadmap/admin/domains/${domain._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Domain deleted");
      fetchDomains();
    } catch (error) {
      console.error("Delete domain error:", error);
      toast.error("Failed to delete domain");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: "#ff6a00",
      tags: "",
      order: 0,
    });
    setEditingDomain(null);
    setShowForm(false);
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "2rem",
            color: "#8b5cf6",
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <button
            onClick={() => navigate("/admin")}
            style={{
              background: "none",
              border: "none",
              color: "#8b5cf6",
              cursor: "pointer",
              marginBottom: "0.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
            }}
          >
            <FaArrowLeft /> Back to Admin Dashboard
          </button>
          <h1 style={{ color: getTextColor(), fontSize: "1.8rem", margin: 0 }}>
            Manage Domains
          </h1>
          <p style={{ color: getTextSecondary(), marginTop: "0.3rem" }}>
            Create and manage learning domains/roadmaps
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: "0.8rem 1.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontWeight: "500",
            }}
          >
            <FaPlus /> Add Domain
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "2rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2 style={{ color: getTextColor(), margin: 0 }}>
              {editingDomain ? "Edit Domain" : "Add New Domain"}
            </h2>
            <button
              onClick={resetForm}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: getTextSecondary(),
              }}
            >
              <FaTimes size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Domain Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., MERN Stack, Java Programming"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe what students will learn in this domain"
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                required
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextColor(),
                  }}
                >
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.3rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    height: "45px",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextColor(),
                  }}
                >
                  Order
                </label>
                <input
                  type="number"
                  value={
                    formData.order === undefined || formData.order === null
                      ? 0
                      : formData.order
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      order:
                        e.target.value === "" ? 0 : parseInt(e.target.value),
                    })
                  }
                  placeholder="Display order (0 = first)"
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) =>
                  setFormData({ ...formData, tags: e.target.value })
                }
                placeholder="e.g., web development, backend, frontend"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <FaSave /> {editingDomain ? "Update" : "Create"} Domain
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Domains List */}
      <div style={{ display: "grid", gap: "1rem" }}>
        {domains.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "20px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaCode size={48} color={getTextSecondary()} />
            <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
              No domains created yet.
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              <FaPlus /> Create your first domain
            </button>
          </div>
        ) : (
          domains.map((domain) => (
            <motion.div
              key={domain._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: getCardBackground(),
                borderRadius: "16px",
                padding: "1rem",
                border: `1px solid ${getBorderColor()}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `${domain.color}20`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaCode size={24} color={domain.color} />
                </div>
                <div>
                  <h3 style={{ color: getTextColor(), marginBottom: "0.3rem" }}>
                    {domain.name}
                  </h3>
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: "0.8rem",
                      maxWidth: "400px",
                    }}
                  >
                    {domain.description?.substring(0, 100)}...
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      marginTop: "0.3rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontSize: "0.7rem", color: domain.color }}>
                      🎨 Color: {domain.color}
                    </span>
                    <span
                      style={{ fontSize: "0.7rem", color: getTextSecondary() }}
                    >
                      📚 {domain.totalTopics} topics
                    </span>
                    <span
                      style={{ fontSize: "0.7rem", color: getTextSecondary() }}
                    >
                      🔢 Order: {domain.order}
                    </span>
                    {domain.tags?.length > 0 && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: getTextSecondary(),
                        }}
                      >
                        🏷️ {domain.tags.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={() =>
                    navigate(
                      `/admin/topics?domainId=${domain._id}&domainName=${encodeURIComponent(domain.name)}`,
                    )
                  }
                  style={{
                    padding: "0.4rem 0.8rem",
                    background: "#17a2b8",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaEye /> Manage Topics
                </button>
                <button
                  onClick={() => {
                    setEditingDomain(domain);
                    setFormData({
                      name: domain.name,
                      description: domain.description,
                      color: domain.color,
                      tags: domain.tags?.join(", ") || "",
                      order: domain.order || 0,
                    });
                    setShowForm(true);
                  }}
                  style={{
                    padding: "0.4rem 0.8rem",
                    background: "#ffc107",
                    color: "#333",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  <FaEdit /> Edit
                </button>
                <button
                  onClick={() => handleDelete(domain)}
                  style={{
                    padding: "0.4rem 0.8rem",
                    background: "#dc3545",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                  }}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminDomains;
